import { createSimpleStore, useSimpleStore } from '@passionware/simple-store';
import { z } from 'zod';
import { Theme, ThemePreference, ThemeService } from './ThemeService';

const STORAGE_KEY = 'v5-theme-preference';
const DEFAULT_PREFERENCE: ThemePreference = 'light';

// Zod schema for theme preference validation
const themePreferenceSchema = z.enum(['light', 'dark', 'system']);

/**
 * Detect the system's preferred color scheme
 */
function getSystemTheme(): Theme {
  if (
    typeof window === 'undefined' ||
    !window.matchMedia ||
    !window.matchMedia('(prefers-color-scheme: dark)').matches
  ) {
    return 'light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

/**
 * Load initial preference from localStorage
 */
function loadPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    // Parse and validate with zod
    return themePreferenceSchema.parse(stored);
  } catch (e) {
    console.warn('ThemeService: Failed to load theme preference', e);
    return DEFAULT_PREFERENCE;
  }
}

/**
 * Save preference to localStorage
 */
function savePreference(preference: ThemePreference): void {
  try {
    localStorage.setItem(STORAGE_KEY, preference);
  } catch (e) {
    console.warn('ThemeService: Failed to save theme preference', e);
  }
}

/**
 * Create the theme service implementation
 */
export function createThemeService(): ThemeService {
  // Store for user preference
  const preferenceStore = createSimpleStore<ThemePreference>(loadPreference());

  // Store for system theme (reactive to OS changes)
  const systemThemeStore = createSimpleStore<Theme>(getSystemTheme());

  // Listen to system theme changes and update the store
  if (typeof window !== 'undefined' && window.matchMedia) {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = () => {
      systemThemeStore.setNewValue(getSystemTheme());
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleSystemThemeChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleSystemThemeChange);
    }
  }

  // Service implementation
  const service: ThemeService = {
    useThemePreference: () => {
      return useSimpleStore(preferenceStore);
    },

    useEffectiveTheme: () => {
      const preference = useSimpleStore(preferenceStore);
      const systemTheme = useSimpleStore(systemThemeStore);

      // Combine preference and system theme to compute effective theme
      return preference === 'system' ? systemTheme : preference;
    },

    setThemePreference: (preference: ThemePreference) => {
      const currentPreference = preferenceStore.getCurrentValue();
      if (preference !== currentPreference) {
        preferenceStore.setNewValue(preference);
        savePreference(preference);
      }
    },

    getThemePreference: () => {
      return preferenceStore.getCurrentValue();
    },

    getEffectiveTheme: () => {
      const preference = preferenceStore.getCurrentValue();
      return preference === 'system'
        ? systemThemeStore.getCurrentValue()
        : preference;
    }
  };

  return service;
}
